import { Redirect, Switch, useRouteMatch } from 'react-router-dom';
import { FundsFormDrawer } from 'assets/js/pages/account-settings/account-settings/FundsFormDrawer';
import { FundsSettings } from 'assets/js/pages/account-settings/account-settings/FundsSettings';
import { GeneralSettings } from 'assets/js/pages/account-settings/account-settings/GeneralSettings';
import { NotificationSettings } from 'assets/js/pages/account-settings/account-settings/NotificationSettings';
import { ShortCodeFormDrawer } from 'assets/js/pages/account-settings/account-settings/ShortCodeFormDrawer';
import { ShortCodeSettings } from 'assets/js/pages/account-settings/account-settings/ShortCodeSettings';
import { TaxDeductibilitySettings } from 'assets/js/pages/account-settings/account-settings/TaxDeductibilitySettings';
import { ThankYouSettings } from 'assets/js/pages/account-settings/account-settings/ThankYouSettings';
import { YearEndSummaries } from 'assets/js/pages/account-settings/account-settings/YearEndSummaries';
import { AppealsFormDrawer } from 'assets/js/pages/account-settings/account-settings/appeals/AppealsFormDrawer';
import { AppealsSettings } from 'assets/js/pages/account-settings/account-settings/appeals/AppealsSettings';
import { DrawerRoute } from 'components/drawer-route';
import * as NavMenu from 'components/nav-menu';
import { Route } from 'components/router/Route';
import { TestIds } from 'constants/tests';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useTaxDeductibleSettings } from 'customHooks/useTaxDeductibleSettings';
import { useFlags } from 'launchdarkly-react-client-sdk';

const SettingsRoutes = {
    TextToDonate: 'text-to-donate',
    ThankYouMessage: 'thank-you-message',
    Funds: 'funds',
    Notifications: 'notifications',
    YearEndSummaries: 'year-end-summaries',
    TaxDeductibility: 'tax-deductibility',
    Appeals: 'appeals',
};

const SettingsRouter = () => {
    const match = useRouteMatch();
    const { isAccountVerified } = useCurrentAccount();
    const { shortCode = false, taxDeductibilitySettings = false, appeals = false } = useFlags();
    const { isEditable: isTaxSettingEditable } = useTaxDeductibleSettings();

    return (
        <div className="grid grid-cols-12 gap-4">
            <NavMenu.Root
                aria-label="Settings"
                orientation="vertical"
                className="col-span-full lg:col-span-3"
            >
                <NavMenu.Item exact={true} to={`${match.url}`}>
                    General
                </NavMenu.Item>
                {(isAccountVerified || shortCode) && (
                    <NavMenu.Item
                        to={`${match.url}/${SettingsRoutes.TextToDonate}`}
                        data-testid={TestIds.AccountSettings.Account.TextToDonate}
                    >
                        Text-to-Donate
                    </NavMenu.Item>
                )}
                <NavMenu.Item
                    to={`${match.url}/${SettingsRoutes.ThankYouMessage}`}
                    data-testid={TestIds.AccountSettings.Account.ThankYouMessage}
                >
                    Thank You Message
                </NavMenu.Item>
                <NavMenu.Item
                    to={`${match.url}/${SettingsRoutes.YearEndSummaries}`}
                    data-testid={TestIds.AccountSettings.Account.EoySummaries}
                >
                    Year-End Summaries
                </NavMenu.Item>
                {taxDeductibilitySettings && isTaxSettingEditable && (
                    <NavMenu.Item
                        to={`${match.url}/${SettingsRoutes.TaxDeductibility}`}
                        data-testid={TestIds.AccountSettings.Account.TaxDeductibility}
                    >
                        Tax Deductibility
                    </NavMenu.Item>
                )}
                {appeals && (
                    <NavMenu.Item
                        to={`${match.url}/${SettingsRoutes.Appeals}`}
                        data-testid={TestIds.AccountSettings.Account.Appeals}
                    >
                        Appeals
                    </NavMenu.Item>
                )}
                <NavMenu.Item
                    to={`${match.url}/${SettingsRoutes.Funds}`}
                    data-testid={TestIds.AccountSettings.Account.Funds}
                >
                    Funds
                </NavMenu.Item>
                <NavMenu.Item
                    to={`${match.url}/${SettingsRoutes.Notifications}`}
                    data-testid={TestIds.AccountSettings.Account.Notifications}
                >
                    My Notifications
                </NavMenu.Item>
            </NavMenu.Root>

            <div className="col-span-full lg:col-span-9">
                <Switch>
                    <Route exact={true} path={match.path} component={GeneralSettings} />
                    {(isAccountVerified || shortCode) && (
                        <Route
                            path={`${match.path}/${SettingsRoutes.TextToDonate}`}
                            component={ShortCodeRouter}
                        />
                    )}
                    <Route
                        path={`${match.path}/${SettingsRoutes.ThankYouMessage}`}
                        component={ThankYouSettings}
                    />
                    <Route
                        path={`${match.path}/${SettingsRoutes.YearEndSummaries}`}
                        component={YearEndSummaries}
                    />
                    {taxDeductibilitySettings && isTaxSettingEditable && (
                        <Route
                            path={`${match.path}/${SettingsRoutes.TaxDeductibility}`}
                            component={TaxDeductibilitySettings}
                        />
                    )}
                    {appeals && (
                        <Route
                            path={`${match.path}/${SettingsRoutes.Appeals}`}
                            component={AppealsRouter}
                        />
                    )}
                    <Route path={`${match.path}/${SettingsRoutes.Funds}`} component={FundsRouter} />
                    <Route
                        path={`${match.path}/${SettingsRoutes.Notifications}`}
                        component={NotificationSettings}
                    />
                    <Route>
                        <Redirect to={match.url} />
                    </Route>
                </Switch>
            </div>
        </div>
    );
};

const FundsRouter = () => {
    const match = useRouteMatch();

    return (
        <>
            <DrawerRoute path={`${match.path}/:fundId/edit`} component={FundsFormDrawer} />
            <DrawerRoute path={`${match.path}/new`} component={FundsFormDrawer} />
            <Switch>
                <Route path={match.path} component={FundsSettings} />
            </Switch>
        </>
    );
};

const AppealsRouter = () => {
    const match = useRouteMatch();

    return (
        <>
            <DrawerRoute
                path={[`${match.path}/new`, `${match.path}/:appealId/edit`]}
                component={AppealsFormDrawer}
            />
            <Switch>
                <Route path={match.path} component={AppealsSettings} />
            </Switch>
        </>
    );
};

const ShortCodeRouter = () => {
    const match = useRouteMatch();

    return (
        <>
            <DrawerRoute path={`${match.path}/:keywordId/edit`} component={ShortCodeFormDrawer} />
            <DrawerRoute path={`${match.path}/new`} component={ShortCodeFormDrawer} />
            <Switch>
                <Route path={match.path} component={ShortCodeSettings} />
            </Switch>
        </>
    );
};

export { SettingsRouter };
